<template>
  <div class="container">
    <main class="main-layout">
      <!-- 左侧上传 -->
      <section class="card upload-section left-card">
        <div class="card-header" style="justify-content: center; align-items: center; height: 80px; display: flex;">
          <h2>Image Upload</h2>
        </div>
        <div class="card-body">
          <div class="upload-form">
            <label for="folderInput" class="file-label">
              <span class="file-icon"><i class="fas fa-folder-open"></i></span>
              <span class="file-text" v-if="folderFiles.length === 0">Click or drag folder</span>
              <span class="file-text" v-else>{{ folderFiles.length }} images selected</span>
              <input type="file" id="folderInput" webkitdirectory directory @change="handleFolderChange" class="file-input">
            </label>

            <button class="btn btn-primary upload-btn" @click="processFolder" :disabled="folderFiles.length === 0 || isProcessing">
              <span v-if="!isProcessing">Batch Segment</span>
              <span v-else><i class="fas fa-spinner fa-spin"></i> Processing...</span>
            </button>

            <button class="btn btn-secondary clear-btn" @click="clearAll" :disabled="isProcessing">
              <i class="fas fa-trash"></i> Clear All
            </button>
          </div>
        </div>
      </section>

      <!-- 右侧：只显示分割图 -->
      <section class="card right-card">
        <div class="card-header" style="justify-content: center; align-items: center; height: 80px; display: flex;">
          <h2>All Results (Segmentation Only)</h2>
        </div>
        <div class="card-body no-scroll-wrapper">
          <div v-if="batchResults.length > 0" class="grid-container">
            <div v-for="(res, idx) in batchResults" :key="idx" class="image-box">
              <img :src="res.annotatedUrl" class="display-image" alt="Segmentation">
              <div class="label">Segmentation</div>
            </div>
          </div>
          <div v-else class="empty-placeholder">
            No segmentation results yet
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const folderFiles = ref([]);
const isProcessing = ref(false);
const batchResults = ref([]);

const handleFolderChange = (e) => {
  const files = Array.from(e.target.files);
  const imageFiles = files.filter(file => file.type.startsWith('image/'));
  folderFiles.value = [];
  
  imageFiles.forEach(file => {
    const reader = new FileReader();
    reader.onload = (e) => {
      file.previewUrl = e.target.result;
      folderFiles.value.push(file);
    };
    reader.readAsDataURL(file);
  });
};

const clearAll = () => {
  folderFiles.value = [];
  batchResults.value = [];
};

// 👇 核心：改为猪分割接口
const processFolder = async () => {
  if (folderFiles.value.length === 0) return;
  isProcessing.value = true;
  batchResults.value = [];

  try {
    // 逐张上传分割（更稳定）
    for (const file of folderFiles.value) {
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch('https://fengguoheng.online/segment', {
        method: 'POST',
        body: formData
      });
      const data = await res.json();

      if (data.overlay_image) {
        batchResults.value.push({
          filename: file.name,
          annotatedUrl: `data:image/jpg;base64,${data.overlay_image}`
        });
      }
    }

    // 保存到本地供结果页使用
    const sessionImages = {
      local: batchResults.value.map((img, i) => ({
        id: i + 1,
        original_filename: img.filename,
        src: img.annotatedUrl
      }))
    };
    localStorage.setItem("sessionImages", JSON.stringify(sessionImages));

  } catch (e) {
    console.error(e);
  } finally {
    isProcessing.value = false;
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
html, body {
  overflow: hidden;
  height: 100%;
  width: 100%;
}
</style>
<style scoped>
.container {
  font-family: 'Segoe UI', Roboto, sans-serif;
  width: 100vw;
  height: 100vh;
  padding: 10px;
  background: #f8fafc;
  overflow: auto !important;
}

.main-layout {
  display: flex;
  gap: 12px;
  height: calc(100% - 300px);
  align-items: stretch;
  max-width: 1400px;
  width: 100%;
  margin-left: auto !important;
  margin-right: auto !important;
}

.left-card {
  width: 340px;
  flex-shrink: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.right-card {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
}

.card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.card-header {
  background: #f1f5f9;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.card-header h2 {
  font-size: 1.1rem;
  color: #1e293b;
}

.card-body {
  padding: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.no-scroll-wrapper {
  overflow: hidden !important;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-label {
  border: 2px dashed #cbd5e1;
  border-radius: 6px;
  padding: 25px 8px;
  text-align: center;
  cursor: pointer;
  background: #f8fafc;
  color: #64748b;
}

.file-label:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.file-icon {
  font-size: 1.6rem;
  margin-bottom: 6px;
}

.file-input {
  display: none;
}

.upload-btn, .clear-btn {
  padding: 9px;
  width: 100%;
  border-radius: 6px;
  border: none;
  font-size: 0.9rem;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-secondary {
  background: #94a3b8;
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 1fr;
  gap: 6px;
  width: 100%;
  height: 100%;
  align-items: stretch;
  justify-items: center;
}

.image-box {
  width: 100%;
  height: 100%;
  position: relative;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.display-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0,0,0,0.6);
  color: white;
  font-size: 9px;
  padding: 1px 0;
  text-align: center;
}

.empty-placeholder {
  color: #94a3b8;
  font-size: 1rem;
}
</style>